/**
 * Voxist Audio SDK - Public API
 * Framework-agnostic audio quality enhancement and streaming for ASR
 *
 * @packageDocumentation
 * @module @voxist/audio-sdk
 */

// Core exports
export { AudioProcessor } from './core/AudioProcessor'
export { AudioQualityEnhancer } from './core/AudioQualityEnhancer'
export type {
  IAudioProcessor,
  TranscriptionSegment,
  AudioConfig,
  ConnectionConfig,
  AudioProcessorConfig,
  QualityEnhancementConfig,
  AGCConfig,
  AdaptiveGainConfig,
  PreEmphasisConfig,
  AuthStrategy,
  StreamConfig
} from './core/types'

// Import types for internal use
import type { QualityEnhancementConfig } from './core/types'
import { AudioQualityEnhancer } from './core/AudioQualityEnhancer'
import {
  WebSocketClient,
  TokenAuthStrategy,
  DirectUrlAuthStrategy
} from './transport/WebSocketClient'
import { AudioStreamManager } from './transport/AudioStreamManager'

// Processing exports
export { AGCProcessor } from './processing/AGCProcessor'
export { DitheringProcessor } from './processing/DitheringProcessor'
export { AdaptiveGainController } from './processing/AdaptiveGainController'
export { PreEmphasisFilter } from './processing/PreEmphasisFilter'

/**
 * Voxist Audio SDK - High-level API builder
 *
 * Provides convenient factory methods for common use cases:
 * - Frontend authentication (apiBase + getToken)
 * - Backend authentication (pre-authenticated wsUrl)
 * - Quality enhancement configuration presets
 *
 * @example Frontend authentication
 * ```typescript
 * const sdk = VoxistAudioSDK.createStreamManager({
 *   apiBase: 'https://api.example.com',
 *   getToken: async () => await auth.getIdToken(),
 *   language: 'fr-FR'
 * });
 *
 * sdk.wsClient.onMessage((transcription) => {
 *   console.log(transcription.text);
 * });
 *
 * await sdk.wsClient.connect();
 * await sdk.startCapture();
 * ```
 *
 * @example Backend authentication
 * ```typescript
 * const wsUrl = await fetch('/api/get-ws-url').then(r => r.json());
 * const sdk = VoxistAudioSDK.createStreamManager({
 *   wsUrl: wsUrl.url,
 *   language: 'fr-FR'
 * });
 * ```
 */
export class VoxistAudioSDK {
  /**
   * Create an AudioStreamManager with convenient defaults
   *
   * @param config Configuration object
   * @param config.apiBase API base URL for frontend auth (mutually exclusive with wsUrl)
   * @param config.wsUrl Pre-authenticated WebSocket URL for backend auth
   *                     (mutually exclusive with apiBase)
   * @param config.getToken Token getter function (required if using apiBase)
   * @param config.language Language code (e.g., 'fr-FR', 'en-US')
   * @param config.model ASR model name (default: 'banafo')
   * @param config.sampleRate Audio sample rate in Hz (default: 16000)
   * @param config.bufferSize Audio buffer size in samples (default: 4096)
   * @param config.engine Engine identifier (default: 'voxist-rt')
   * @param config.enableQualityEnhancement Enable audio quality improvements (default: true)
   * @param config.qualityConfig Quality enhancement configuration
   *                             (default: all enhancements enabled)
   *
   * @returns Object with wsClient (WebSocketClient) and streamManager (AudioStreamManager)
   *
   * @throws Error if both apiBase and wsUrl are provided
   * @throws Error if apiBase provided without getToken
   */
  static createStreamManager(config: {
    apiBase?: string
    wsUrl?: string
    getToken?: () => Promise<string>
    language: string
    model?: string
    sampleRate?: number
    bufferSize?: number
    engine?: string
    enableQualityEnhancement?: boolean
    qualityConfig?: QualityEnhancementConfig
    punctuationMode?: 'Generated' | 'Dictated'
  }): { wsClient: WebSocketClient; streamManager: AudioStreamManager } {
    // Validate configuration
    if (config.apiBase && config.wsUrl) {
      throw new Error(
        'Cannot provide both apiBase and wsUrl. Use one authentication method.'
      )
    }

    if (config.apiBase && !config.getToken) {
      throw new Error('getToken is required when using apiBase authentication')
    }

    // Determine authentication strategy
    const authStrategy = config.wsUrl
      ? new DirectUrlAuthStrategy(config.wsUrl)
      : new TokenAuthStrategy(
          config.apiBase || 'http://localhost:5005',
          config.getToken!,
          config.engine || 'voxist-rt'
        )

    // Create WebSocket client
    const wsClient = new WebSocketClient(authStrategy, {
      language: config.language,
      sampleRate: config.sampleRate ?? 16000,
      model: config.model ?? 'banafo',
      bufferSize: config.bufferSize ?? 4096,
      punctuationMode: config.punctuationMode
    })

    // Determine quality configuration
    let qualityConfig = config.qualityConfig
    if (!qualityConfig && config.enableQualityEnhancement) {
      // Use default config (all enhancements enabled)
      qualityConfig = AudioQualityEnhancer.createDefaultConfig()
    }

    // Create stream manager
    const streamManager = new AudioStreamManager(wsClient, {
      sampleRate: config.sampleRate ?? 16000,
      bufferSize: config.bufferSize ?? 4096,
      language: config.language,
      model: config.model ?? 'banafo',
      enableQualityEnhancement: config.enableQualityEnhancement ?? true,
      qualityConfig
    })

    return { wsClient, streamManager }
  }

  /**
   * Create default quality configuration (all enhancements enabled)
   * Provides maximum quality improvement (~60-80%)
   */
  static createDefaultQualityConfig(): QualityEnhancementConfig {
    return AudioQualityEnhancer.createDefaultConfig()
  }

  /**
   * Create conservative quality configuration (AGC only)
   * Provides significant improvement (~40-60%) with lowest risk
   */
  static createConservativeQualityConfig(): QualityEnhancementConfig {
    return AudioQualityEnhancer.createConservativeConfig()
  }

  /**
   * Create recommended quality configuration (AGC + Dithering)
   * Best balance of quality (~50-75%) and safety
   */
  static createRecommendedQualityConfig(): QualityEnhancementConfig {
    return AudioQualityEnhancer.createRecommendedConfig()
  }
}
